import type { ReactElement } from 'react'
import { Link } from '@tanstack/react-router'
import { registryKinds } from '../lib/registry-api.ts'
import type { RegistryKind } from '../lib/registry-api.ts'

/**
 * `agent-roles` -> `Agent roles`, and the singular for the create button.
 *
 * Written out rather than derived. The old version de-slugged and
 * upper-cased the first letter, which turned `cli-profiles` into "Cli
 * profiles" and `mcp-definitions` into "Mcp definitions" — an acronym no
 * mechanical rule gets right — and there is no pluralisation rule that
 * gets both `harnesses` -> harness and `agent-roles` -> agent role.
 *
 * Two labels per kind because the page needs both: the plural names the
 * page ("Harnesses"), the singular names the action ("New harness"). The
 * `Record` makes a kind added to the API a type error here rather than a
 * page titled with a slug.
 */
const KIND_LABELS: Record<RegistryKind, { plural: string; singular: string }> =
  {
    harnesses: { plural: 'Harnesses', singular: 'harness' },
    'cli-profiles': { plural: 'CLI profiles', singular: 'CLI profile' },
    'failover-configs': {
      plural: 'Failover configs',
      singular: 'failover config',
    },
    'agent-roles': { plural: 'Agent roles', singular: 'agent role' },
    'agent-definitions': {
      plural: 'Agent definitions',
      singular: 'agent definition',
    },
    'mcp-definitions': {
      plural: 'MCP definitions',
      singular: 'MCP definition',
    },
    'skill-definitions': {
      plural: 'Skill definitions',
      singular: 'skill definition',
    },
  }

export function registryKindLabel(kind: RegistryKind): string {
  return KIND_LABELS[kind].plural
}

/** The singular, lower case, for "New <thing>" and "No <things> yet". */
export function registryKindSingular(kind: RegistryKind): string {
  return KIND_LABELS[kind].singular
}

/**
 * The switcher across the seven registry kinds.
 *
 * This is page content, not app chrome: the sidebar has a single
 * "Registry" entry pointing at harnesses, and the kinds are a dimension
 * of this page rather than seven destinations in the nav. It sits in
 * `PageHeader`'s breadcrumb slot for that reason — above the title, where
 * "which of these am I looking at" belongs.
 *
 * `aria-current="page"` rather than weight alone: the active kind has to
 * be announced, not just look heavier.
 */
export function RegistryKindNav({
  kind,
}: {
  kind: RegistryKind
}): ReactElement {
  return (
    <nav aria-label="Registry sections">
      <ul className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {registryKinds.map((candidate) => {
          const isActive = candidate === kind
          return (
            <li key={candidate}>
              <Link
                to="/registry/$kind"
                params={{ kind: candidate }}
                aria-current={isActive ? 'page' : undefined}
                className={`rounded-sm text-xs transition-colors ${
                  isActive
                    ? 'font-medium text-fg underline underline-offset-4'
                    : 'text-muted hover:text-fg'
                }`}
              >
                {registryKindLabel(candidate)}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
